import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { Observable, of } from 'rxjs';
import { mergeMap, map, tap } from 'rxjs/operators';
import { GenerateReportModel } from "../../reports/model/reports.model";


import { ApiService, ParamList } from '../../common/services/api.service';
import { Mapper } from "../services/mapper.service";
import { OnBehalfMapperService } from "../services/onbehalf-mapper.service";
import { WorkOrderService } from "../../common/services/work-order.service";

import * as dto from "../../common/models/flow.dto";
import * as model from "../models/flow.model";

@Injectable()
export class FlowInstanceService {

    static readonly BaseUrl = '/api/flow';


    constructor(public http: HttpClient,
        public router: Router,
        public mapper: Mapper,
        public onBehalfMapper: OnBehalfMapperService,
        public apiService: ApiService) { }

    create(id: string, templateId: string): Observable<model.FlowInstance> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/new/${templateId}`, {})
            .pipe(
                map(x => this.transformModel(x.instance, x.context, this.mapper)
        ));
    }

    deleteTemplate(id: string): Observable<any> {
        return this.http.delete<any>(`${FlowInstanceService.BaseUrl}/template/${id}`);
    }

    get(id: string): Observable<model.FlowInstance> {
        return this.getByWorkItem(id, null);
    }

    getByWorkItem(id: string, workItemId: string): Observable<model.FlowInstance> {
        return this.getInstance(id, workItemId).pipe(
            mergeMap((obj) => {
                let flow = this.transformModel(obj.instance, obj.context, this.mapper);
                return [flow];
            }));
    }

    getOnBehalf(id: string): Observable<model.FlowInstance> {
        return this.getInstance(id, null).pipe(
            mergeMap((obj) => {
                let flow = this.transformModel(obj.instance, obj.context, this.onBehalfMapper); 
                return [flow];
            })
        );
    }

    getContext(id: string): Observable<any> {
        return this.http.get<any>(`${FlowInstanceService.BaseUrl}/${id}/context`);
    }

    getInstance(id: string, workItemId: string): Observable<dto.WrappedFlow> {
        if (workItemId) {
            return this.http.get<dto.WrappedFlow>(`${FlowInstanceService.BaseUrl}/${id}/${workItemId}`);
        }
        return this.http.get<dto.WrappedFlow>(`${FlowInstanceService.BaseUrl}/${id}`);
    }

    getLatestPublishedTemplate(id: string): Observable<dto.FlowTemplate> {
        return this.http.get<dto.FlowTemplate>(`${FlowInstanceService.BaseUrl}/template/published/${id}`);
    }

    getTemplate(id: string): Observable<dto.FlowTemplate> {
        return this.http.get<dto.FlowTemplate>(`${FlowInstanceService.BaseUrl}/template/${id}`);
    }

    recordSignature(signature: dto.Signature): Observable<any> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/sign`, signature);
    }

    recordChiefSignature(signature: dto.Signature): Observable<any> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/sign/chief`, signature);
    }

    saveFullContext(id: string, context: any): Observable<any> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/${id}/context`, context);
    }

    public completeWorkItem(id: string, workItemId: string): Observable<any> {
        return this.http.post<any>(`${WorkOrderService.BaseUrl}/complete/${workItemId}`, {});
    }

    public returnWorkItem(id: string, workItemId: string): Observable<any> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/${id}/return/${workItemId}`, {});
    }

    //do not ever call this directly. this should only ever be called through workflow.saveContext();
    public patchSaveContext(id: string, context: any): Observable<any> {
        return this.http.patch<any>(`${FlowInstanceService.BaseUrl}/${id}/context`, context);
    }

     //restoring this to patch will require corresponding back-end changes for the http action to match
    //public executeTransition(id: string, button: model.Button, transition: string, context: any): Observable<any> {
    //    return this.http.patch<any>(`${FlowInstanceService.BaseUrl}/${id}/invoke/${button.id}/${transition}`, context);
    //}

    executeTransition(id: string, button: model.Button, transition: string, context: any): Observable<any> {
        return this.http.post<any>(`${FlowInstanceService.BaseUrl}/${id}/invoke/${button.id}/${transition}`, context);
    }

    public transformModel(dto: dto.FlowInstance, ctx: any, mapService: Mapper): model.FlowInstance {
        let model = mapService.createInstance(dto, ctx);
        let service = this;


        model.saveContext = function () {
            return service.saveFullContext(model.id, model.context);//.toPromise();
        }

        model.executeTransition = function (state: model.State, button: model.TransitionButton, transition: model.Transition): Promise<any> {
            return service.executeTransition(model.id, button, transition.name, model.context).toPromise().then((destination) => {
                return destination;
            }, (fail) => { console.log(fail); });
        }

        model.saveFullContext = (): Observable<any> => {
            return service.saveFullContext(model.id, model.context);
        }

        model.returnWorkItem = function (workItem: model.WorkOrder): Promise<any> {
            return service.returnWorkItem(this.id, workItem.id).toPromise();
        }

        model.completeWorkItem = function (workItem: model.WorkOrder): Promise<any> {
            return service.completeWorkItem(this.id, workItem.id).toPromise();
        }

        model.gotoLocalTarget = function (state: model.State, transition: model.Transition): Promise<any> {
            return service.gotoLocalTarget(this.id, state, transition);
        };

        return model;
    }

    public gotoLocalTarget(flow: model.FlowInstance, state: model.State, transition: model.Transition): Promise<any> {
        alert("Not sure what to do here");
        return this.router.navigate(['/home']);
    }

    queryFlowIndices(queryName: string, orgId: string | undefined | null = null, queryParams: Map<string, string> = new Map<string, string>()): Observable<dto.FlowIndex[]> {
        const hasOrgId = (orgId != null);
        const queryString = this.apiService.buildParameterListFromMap(queryParams, !hasOrgId);
        let uri = `/api/flow-index/queries/${queryName}${orgId != null ? "?orgId=" + orgId : ""}`;
        if (queryParams) uri += queryString;
        return this.http.get<dto.FlowIndex[]>(uri);
    }

    getReturnedIncompleteApplication(queryName: string,isReturned:boolean, orgId: string | undefined | null = null, queryParams: Map<string, string> = new Map<string, string>): Observable<dto.FlowIndex[]> {
        const hasOrgId = (orgId != null);

        const queryString = this.apiService.buildParameterListFromMap(queryParams, !hasOrgId);
        let uri = `/api/flow-index/queries/${queryName}/${isReturned}${orgId != null ? "?orgId=" + orgId : ""}`;
        if (queryParams) uri += queryString;
        return this.http.get<dto.FlowIndex[]>(uri);
    }

    getSubmittedIncompleteApplications(specName: string): Observable<dto.FlowIndex[]> {
        let uri = `/api/flow-index/queries/submitted-incomplete/${specName}`;
        return this.http.get<dto.FlowIndex[]>(uri);
    }

    findFlowIndicies(spec: FlowIndexSpec): Observable<dto.FlowIndex[]> {
        return this.http.get<dto.FlowIndex[]>(`/api/flow-index?${this._buildQueryString(spec)}`);
    }

    findFlowIndex(flowId: string): Observable<dto.FlowIndex> {
        return this.http.get<dto.FlowIndex>(`/api/flow-index/find/${flowId}`);
    }

    assignFlowIndex(flowId: string, userId: string | null, userName: string | null): Observable<dto.FlowIndex> {
        return this.http.post<dto.FlowIndex>(`/api/flow-index/assign`, { flowId, userId, userName });
    }

    setPaymentReceivedDates(req: model.PaymentReceivedDateRequest): Observable<any[]> {
        return this.http.post<any[]>("/api/flow/set-payment-received-date", req);
    }

    generatePdfSummary(model: GenerateReportModel): Observable<any> {
        const opt = {
            headers: new HttpHeaders({ "Content-Type": "application/json", "Accept": "application/pdf" }),
            responseType: 'blob' as 'json'
        };
        return this.http.post<any>("/api/reports/summary-pdf", model, opt);
    }

    public _buildQueryString(criteria: FlowIndexSpec): string {

        let params = new URLSearchParams();
        if (criteria.organizationId) {
            params.set("orgId", criteria.organizationId);
        }

        if (criteria.flowIdentifier) {
            params.set("flowIdentifier", criteria.flowIdentifier);
        }

        if (criteria.isSubmitted !== undefined && criteria.isSubmitted !== null) {
            params.set("isSubmitted", criteria.isSubmitted.toString());
        }

        if (criteria.isInReview !== undefined && criteria.isInReview !== null) {
            params.set("isInReview", criteria.isInReview.toString());
        }

        if (criteria.isOnBehalf !== undefined && criteria.isOnBehalf !== null) {
            params.set("isOnBehalf", criteria.isOnBehalf.toString());
        }

        for (let i = 0; i < criteria.parts.length; i++) {
            let part = criteria.parts[i];

            if (part.templateId) {
                let prefix = `parts[${i}]`;
                params.set(prefix + ".templateId", part.templateId);
                if (part.inAnyState) {
                    params.set(prefix + ".inAnyState", part.inAnyState ? 'true' : 'false');
                }
                if (!part.inAnyState && part.states && part.states.length > 0) {
                    for (let j = 0; j < part.states.length; j++) {
                        let key = `${prefix}.states[${j}]`;
                        params.set(key, part.states[j]);
                    }
                }
            }
        }
        return params.toString();
    }

    //Method created/kept to retain how the patch was set up, should we ever try to resurrect it
    //private setupPatch(model: model.FlowInstance, service: FlowInstanceService): model.FlowInstance {
    //    let observer = jsonpatch.observe(model.context);
    //    model.saveContext = function () {
    //        let patch = jsonpatch.generate(observer);
    //        if(patch.length == 0){
    //            return new Promise((rs,er)=>{
    //                rs({});
    //            });
    //        }
    //        return service.patchSaveContext(this.id, patch).toPromise()
    //            .then(success => {
    //                console.log(observer);
    //            observer = jsonpatch.observe(model.context);
    //        }, fail => {});

    //    };

    //    model.executeTransition = function (state: model.State, button: model.TransitionButton, transition: model.Transition): Promise<any> {
    //        let patch = jsonpatch.generate(observer);
    //        return service.executeTransition(this.id, button, transition.name, patch).toPromise().then((destination) =>{
    //            observer = jsonpatch.observe(model.context);
    //            console.log(`target: ${destination}`);
    //            return destination;
    //        }, fail=>{});;
    //    };
    //    return model;
    //}
}

export class FlowIndexSpecItem {
    templateId: string;
    states: string[] = [];
    inAnyState: boolean = false;
}

export class FlowIndexSpec {
    additionalQueryTerm?: string;
    allowPublic?: boolean;
    filterWorkOrderAssignment?: boolean;
    flowIdentifier: string;
    isOnBehalf?: boolean;
    isInReview?: boolean;
    isSubmitted?: boolean;
    organizationId: string;
    ownedFlowsOnly?: boolean;    
    parts: FlowIndexSpecItem[] = [];

    constructor() {  }
}
